import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, Store, Wrench, HandHelping, ArrowLeft, ImagePlus, X, ShieldAlert } from "lucide-react";
import api, { apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PhoneVerifyDialog } from "@/components/dialogs";
import { cn, Spinner } from "@/components/common";

const OPTIONS = [
  { type: "sell", label: "Sell something", icon: ShoppingBag, desc: "List an item for sale", needsPhone: true },
  { type: "business", label: "Advertise my business", icon: Store, desc: "Create a business profile", needsPhone: true },
  { type: "service", label: "Offer a service", icon: Wrench, desc: "List yourself as a provider", needsPhone: true },
  { type: "request", label: "I need someone", icon: HandHelping, desc: "Ask the community for help", needsPhone: false },
];

function ImageUploader({ photos, setPhotos, max = 6 }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    setUploading(true);
    try {
      for (const f of files.slice(0, max - photos.length)) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, data.path]);
      }
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };
  return (
    <div>
      <Label>Photos</Label>
      <div className="mt-2 flex flex-wrap gap-2">
        {photos.map((p) => (
          <div key={p} className="relative h-20 w-20 overflow-hidden rounded-lg border border-border">
            <img src={fileUrl(p)} alt="" className="h-full w-full object-cover" />
            <button onClick={() => setPhotos((x) => x.filter((y) => y !== p))} className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white" data-testid="remove-photo"><X className="h-3 w-3" /></button>
          </div>
        ))}
        {photos.length < max && (
          <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground hover:border-primary" data-testid="photo-upload">
            {uploading ? <Spinner /> : <><ImagePlus className="h-5 w-5" /><span className="text-[10px]">Add</span></>}
            <input type="file" accept="image/*" multiple className="hidden" onChange={upload} />
          </label>
        )}
      </div>
    </div>
  );
}

function FormShell({ title, children, onSubmit, loading, back }) {
  return (
    <div className="mx-auto max-w-lg space-y-5">
      <button onClick={back} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="post-back"><ArrowLeft className="h-4 w-4" />Back</button>
      <h1 className="font-display text-2xl font-bold tracking-tight">{title}</h1>
      <form onSubmit={onSubmit} className="space-y-4">{children}
        <Button type="submit" className="w-full" size="lg" disabled={loading} data-testid="post-submit">{loading ? <Spinner className="text-white" /> : "Publish"}</Button>
      </form>
    </div>
  );
}

export default function Post() {
  const nav = useNavigate();
  const { type } = useParams();
  const { user, config } = useAuth();
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingType, setPendingType] = useState(null);

  const pick = (opt) => {
    if (opt.needsPhone && !user.phone_verified) { setPendingType(opt.type); setPhoneOpen(true); return; }
    nav(`/post/${opt.type}`);
  };

  if (!type) {
    return (
      <div className="mx-auto max-w-lg space-y-4">
        <h1 className="font-display text-2xl font-bold tracking-tight">What would you like to post?</h1>
        {!user.phone_verified && (
          <div className="flex items-start gap-2 rounded-xl border border-amber-300/60 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-200">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            Selling, business and service posts need a verified phone. You'll be prompted to verify.
          </div>
        )}
        <div className="grid gap-3">
          {OPTIONS.map((o) => {
            const Icon = o.icon;
            return (
              <button key={o.type} onClick={() => pick(o)} data-testid={`post-option-${o.type}`}
                className="card-hover flex items-center gap-4 rounded-2xl border border-border bg-card p-4 text-left">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><Icon className="h-6 w-6" /></div>
                <div className="flex-1"><h3 className="font-display font-semibold">{o.label}</h3><p className="text-sm text-muted-foreground">{o.desc}</p></div>
              </button>
            );
          })}
        </div>
        <PhoneVerifyDialog open={phoneOpen} onOpenChange={setPhoneOpen} onVerified={() => pendingType && nav(`/post/${pendingType}`)} />
      </div>
    );
  }

  const back = () => nav("/post");
  const states = config?.states || [];
  const handle = (path, payload, redirect) => async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post(path, payload);
      const mod = data.moderation;
      if (mod?.status === "pending_review") toast.warning("Posted — under review. Our team will check it shortly for safety.");
      else toast.success("Published successfully 🎉");
      nav(redirect(data));
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  if (type === "sell") return <SellForm states={states} cats={config?.marketplace_categories || []} handle={handle} loading={loading} back={back} />;
  if (type === "business") return <BusinessForm states={states} cats={config?.business_categories || []} handle={handle} loading={loading} back={back} />;
  if (type === "service") return <ServiceForm states={states} cats={config?.service_categories || []} handle={handle} loading={loading} back={back} />;
  if (type === "request") return <RequestForm states={states} cats={config?.service_categories || []} handle={handle} loading={loading} back={back} />;
  return null;
}

function SellForm({ states, cats, handle, loading, back }) {
  const [f, setF] = useState({ title: "", price: "", negotiable: false, description: "", condition: "Used", category: "", state: "", suburb: "" });
  const [photos, setPhotos] = useState([]);
  return (
    <FormShell title="Sell something" loading={loading} back={back}
      onSubmit={handle("/marketplace", { ...f, price: parseFloat(f.price) || 0, photos }, (d) => `/listing/${d.listing.id}`)}>
      <div><Label>Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required data-testid="sell-title" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Price (AUD)</Label><Input type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="sell-price" /></div>
        <div><Label>Condition</Label>
          <Select value={f.condition} onValueChange={(v) => setF({ ...f, condition: v })}><SelectTrigger data-testid="sell-condition"><SelectValue /></SelectTrigger>
            <SelectContent>{["New", "Like New", "Used", "For parts"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <label className="flex items-center gap-2 text-sm"><Switch checked={f.negotiable} onCheckedChange={(v) => setF({ ...f, negotiable: v })} data-testid="sell-negotiable" />Price negotiable</label>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Category</Label><Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger data-testid="sell-category"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>State</Label><Select value={f.state} onValueChange={(v) => setF({ ...f, state: v })}><SelectTrigger data-testid="sell-state"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <div><Label>Suburb</Label><Input value={f.suburb} onChange={(e) => setF({ ...f, suburb: e.target.value })} data-testid="sell-suburb" /></div>
      <div><Label>Description</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={4} data-testid="sell-description" /></div>
      <ImageUploader photos={photos} setPhotos={setPhotos} />
    </FormShell>
  );
}

function BusinessForm({ states, cats, handle, loading, back }) {
  const [f, setF] = useState({ name: "", category: "", description: "", state: "", suburb: "", phone: "", website: "", whatsapp: "", email: "" });
  const [cover, setCover] = useState([]);
  return (
    <FormShell title="Advertise my business" loading={loading} back={back}
      onSubmit={handle("/businesses", { ...f, cover_url: cover[0] || null }, (d) => `/business/${d.business.id}`)}>
      <div><Label>Business name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="biz-name" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Category</Label><Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger data-testid="biz-category"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>State</Label><Select value={f.state} onValueChange={(v) => setF({ ...f, state: v })}><SelectTrigger data-testid="biz-state"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <div><Label>Suburb</Label><Input value={f.suburb} onChange={(e) => setF({ ...f, suburb: e.target.value })} data-testid="biz-suburb" /></div>
      <div><Label>Description</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3} data-testid="biz-description" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="biz-phone" /></div>
        <div><Label>WhatsApp</Label><Input value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} data-testid="biz-whatsapp" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Website</Label><Input value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} data-testid="biz-website" /></div>
        <div><Label>Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="biz-email" /></div>
      </div>
      <ImageUploader photos={cover} setPhotos={setCover} max={1} />
    </FormShell>
  );
}

function ServiceForm({ states, cats, handle, loading, back }) {
  const [f, setF] = useState({ name: "", category: "", description: "", state: "", suburb: "", phone: "", whatsapp: "", price_info: "" });
  const [photos, setPhotos] = useState([]);
  return (
    <FormShell title="Offer a service" loading={loading} back={back}
      onSubmit={handle("/services", { ...f, photos }, (d) => `/service/${d.service.id}`)}>
      <div><Label>Service name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="svc-name" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Category</Label><Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger data-testid="svc-category"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>State</Label><Select value={f.state} onValueChange={(v) => setF({ ...f, state: v })}><SelectTrigger data-testid="svc-state"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <div><Label>Description</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={4} data-testid="svc-description" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="svc-phone" /></div>
        <div><Label>Pricing info</Label><Input value={f.price_info} onChange={(e) => setF({ ...f, price_info: e.target.value })} data-testid="svc-price" /></div>
      </div>
      <ImageUploader photos={photos} setPhotos={setPhotos} />
    </FormShell>
  );
}

function RequestForm({ states, cats, handle, loading, back }) {
  const [f, setF] = useState({ title: "", category: "", state: "", suburb: "", date_needed: "", budget: "", description: "" });
  const [photos, setPhotos] = useState([]);
  return (
    <FormShell title="I need someone" loading={loading} back={back}
      onSubmit={handle("/requests", { ...f, budget: f.budget ? parseFloat(f.budget) : null, photos }, (d) => `/request/${d.request.id}`)}>
      <div><Label>What do you need?</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. I need a photographer this Saturday" required data-testid="req-title" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Category</Label><Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger data-testid="req-category"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>State</Label><Select value={f.state} onValueChange={(v) => setF({ ...f, state: v })}><SelectTrigger data-testid="req-state"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Suburb</Label><Input value={f.suburb} onChange={(e) => setF({ ...f, suburb: e.target.value })} data-testid="req-suburb" /></div>
        <div><Label>Budget (optional)</Label><Input type="number" value={f.budget} onChange={(e) => setF({ ...f, budget: e.target.value })} data-testid="req-budget" /></div>
      </div>
      <div><Label>When (optional)</Label><Input value={f.date_needed} onChange={(e) => setF({ ...f, date_needed: e.target.value })} placeholder="e.g. This Saturday" data-testid="req-date" /></div>
      <div><Label>Details</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={4} data-testid="req-description" /></div>
      <ImageUploader photos={photos} setPhotos={setPhotos} />
    </FormShell>
  );
}
